import React, { Fragment } from 'react';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import { toAbsoluteUrl } from '@/utils';
import { MatchDetailsData } from '@/pages/buddy/types/MatchDetailsData.ts';
import { CommonHexagonBadge } from '@/partials/common';
import { getProgressColor } from '@/pages/buddy/utils/ProgressColor.tsx';
import { Link, useNavigate } from 'react-router-dom';

interface MatchProfileHeroProps {
    match: MatchDetailsData;
}

const MatchProfileHero: React.FC<MatchProfileHeroProps> = ({ match }) => {
    const progressPercentage = Math.round((match.overall_soft_score_relative ?? 0) * 100);
    const navigate = useNavigate();

    return (
        <Fragment>
            <style>
                {`
          .hero-bg {
            background-image: url('${toAbsoluteUrl('/media/images/2600x1200/bg-1.png')}');
          }
          .dark .hero-bg {
            background-image: url('${toAbsoluteUrl('/media/images/2600x1200/bg-1-dark.png')}');
          }
        `}
            </style>
            <div className="bg-center bg-cover bg-no-repeat hero-bg">
                <Container>
                    <div className="grid lg:grid-cols-3 gap-5 lg:gap-7.5">
                        {/* Candidate Info */}
                        <div className="lg:col-span-1">
                            <div className="flex flex-col items-center gap-2 lg:gap-3 py-4 lg:py-5">
                                <div className="flex items-center justify-center rounded-full border-2 border-primary bg-light h-[100px] w-[100px]">
                                    {match.candidate_image ? (
                                        <img
                                            src={toAbsoluteUrl(match.candidate_image)}
                                            alt={match.candidate_last_name || 'Candidate image'}
                                            className="size-[50px] object-cover"
                                        />
                                    ) : (
                                        <KeenIcon icon="user" className="default-color text-3xl" />
                                    )}
                                </div>

                                {/* Clickable Candidate Name */}
                                <Link
                                    to={`/candidate/${match.candidate_p_id}`}
                                    className="text-2xl font-semibold text-gray-800 no-underline hover:text-primary text-center"
                                >
                                    {match.candidate_first_name} {match.candidate_last_name}
                                </Link>

                                {match.candidate_email && (
                                    <div className="flex items-center gap-1 text-gray-600 text-sm">
                                        <KeenIcon icon="business" className="text-primary" />
                                        <KeenIcon icon="sms" className="text-primary" />

                                        <a
                                            href={`mailto:${match.candidate_email}`}
                                            className="no-underline hover:text-primary"
                                        >
                                            {match.candidate_email}
                                        </a>

                                        <KeenIcon icon="phone" className="text-primary" />

                                        <a
                                            href={`tel:${match.candidate_phone}`}
                                            className="no-underline hover:text-primary"
                                        >
                                            {match.candidate_phone}
                                        </a>
                                    </div>
                                )}

                                <div className="flex flex-col md:flex-row gap-2 items-center justify-center mt-2">
                                    {match.candidate_country && (
                                        <div className="flex items-center gap-1 text-gray-600 text-sm">
                                            <KeenIcon icon="tag" className="text-primary" />
                                            <span>
                        {match.candidate_address}, {match.candidate_country}
                      </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Match Score */}
                        <div className="lg:col-span-1">
                            <Container>
                                 <div className="flex flex-col items-center gap-2 lg:gap-3 py-4 lg:py-10">
                                     {/*{match.hard_criteria_passed ? (
                                        <>
                                            <CommonHexagonBadge
                                                stroke="stroke-success-clarity"
                                                fill="fill-success-light"
                                                size="size-[50px]"
                                                badge={<KeenIcon icon="abstract-23" className="text-success text-1.5xl ps-px" />}
                                            />
                                            <span className="text-success font-medium">
                        Hard Criteria Met
                      </span>
                                        </>
                                    ) : (
                                        <>
                                            <CommonHexagonBadge
                                                stroke="stroke-danger-clarity"
                                                fill="fill-danger-light"
                                                size="size-[50px]"
                                                badge={<KeenIcon icon="abstract-11" className="text-danger text-1.5xl ps-px" />}
                                            />
                                            <span className="text-danger font-medium">
                        Hard Criteria Not Met
                      </span>
                                        </>
                                    )}*/}
                                </div>

                                <div className="text-2xl font-semibold text-gray-800 text-center">
                                    {progressPercentage}%
                                </div>

                                {/* Display absolute/max score */}
                                {(match.overall_soft_score_absolute !== undefined &&
                                    match.overall_soft_score_absolute_max !== undefined) && (
                                    <div className="text-sm text-gray-700 text-center mb-2.5">
                                        {match.overall_soft_score_absolute} of {match.overall_soft_score_absolute_max} Points
                                    </div>
                                )}

                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="h-2.5 rounded-full"
                                        style={{
                                            width: `${progressPercentage}%`,
                                            backgroundColor: getProgressColor(progressPercentage),
                                        }}
                                    />
                                </div>
                            </Container>
                        </div>

                        {/* Job Info */}
                        <div className="lg:col-span-1">
                            <Container>
                                <div className="flex flex-col items-center gap-2 lg:gap-3 py-4 lg:py-5">
                                    {/* Company Image and Name */}
                                    {match.company_name && match.company_p_id && (
                                        <div className="flex flex-col items-center text-gray-600 text-sm mt-2 cursor-pointer">
                                            <div
                                                className="flex items-center justify-center rounded-full border-2 border-primary bg-light h-[50px] w-[50px] hover:border-gray-800"
                                                onClick={() => navigate(`/company/${match.company_p_id}`)}
                                            >
                                                {match.company_image ? (
                                                    <img
                                                        src={toAbsoluteUrl(match.company_image)}
                                                        alt={match.company_name || 'Company image'}
                                                        className="h-[25px] w-[25px] object-cover rounded-full"
                                                    />
                                                ) : (
                                                    <KeenIcon icon="home" className="default-color text-xl" />
                                                )}
                                            </div>

                                            <span
                                                onClick={() => navigate(`/company/${match.company_p_id}`)}
                                                className="text-xl font-semibold text-center text-primary hover:text-gray-800 cursor-pointer mt-1"
                                            >
                        {match.company_name}
                      </span>
                                        </div>
                                    )}

                                    {/* Clickable Job Name */}
                                    <Link
                                        to={`/job/${match.job_p_id}`}
                                        className="text-2xl font-semibold text-gray-800 no-underline hover:text-primary text-center"
                                    >
                                        {match.job_name}
                                    </Link>

                                    {/* Job Details */}
                                    <div className="flex flex-col md:flex-row gap-2 items-center justify-center mt-2">
                                        {match.job_location && (
                                            <div className="flex items-center gap-1 text-gray-600 text-sm">
                                                <KeenIcon icon="geolocation" className="text-primary" />
                                                <span>
                          {match.job_remote ? 'Remote' : match.job_location.toString()}
                        </span>
                                            </div>
                                        )}
                                        {match.job_salary_range && (
                                            <div className="flex items-center gap-1 text-gray-600 text-sm">
                                                <KeenIcon icon="tag" className="text-primary" />
                                                <span>{match.job_salary_range}</span>
                                            </div>
                                        )}
                                        {match.job_employment_type && (
                                            <div className="flex items-center gap-1 text-gray-600 text-sm">
                                                <KeenIcon icon="watch" className="text-primary" />
                                                <span>{match.job_employment_type}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Container>
                        </div>
                    </div>
                </Container>
            </div>
        </Fragment>
    );
};

export { MatchProfileHero };
